import { MapPin, CheckCircle2 } from 'lucide-react';

import { useClassEnrollments } from '../../../hooks/schedule/useClassEnrollments';
import type { ScheduleEvent } from '../../../services/schedule';

interface ClassCardProps {
  event: ScheduleEvent;
  isEnrolled: boolean;
  isCheckedIn: boolean;
  onClick: () => void;
  view?: 'week' | 'day';
}

function formatTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function capacityColor(current: number | null, max: number | null): string {
  if (!max) return 'text-[#A9A3A3]';
  const ratio = (current ?? 0) / max;
  if (ratio >= 1) return 'text-red-400';
  if (ratio >= 0.8) return 'text-yellow-400';
  return 'text-green-400';
}

function initialFor(name?: string | null): string {
  const trimmed = (name ?? '').trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
}

interface AvatarBubbleProps {
  src?: string | null;
  name?: string | null;
  size: 'sm' | 'md';
  ring?: 'card' | 'enrolled';
  title?: string;
}

function AvatarBubble({ src, name, size, ring = 'card', title }: AvatarBubbleProps) {
  const dim = size === 'md' ? 'w-6 h-6 text-[10px]' : 'w-5 h-5 text-[9px]';
  // Ring matches card background so overlapped avatars read as separate circles.
  const ringClass = ring === 'enrolled' ? 'ring-[#3B1A1F]' : 'ring-[#2A2626]';
  return (
    <div
      title={title ?? name ?? undefined}
      className={`${dim} rounded-full ring-2 ${ringClass} bg-[#504A4A] text-[#EBFFEE] font-semibold flex items-center justify-center overflow-hidden shrink-0`}
    >
      {src ? (
        <img src={src} alt={name ?? ''} className="w-full h-full object-cover" />
      ) : (
        <span>{initialFor(name)}</span>
      )}
    </div>
  );
}

export function ClassCard({ event, isEnrolled, isCheckedIn, onClick, view = 'week' }: ClassCardProps) {
  const isFull =
    event.max_capacity !== null &&
    (event.current_registrations ?? 0) >= event.max_capacity;

  const instructorNames = event.instructor_names ?? [];
  const instructorAvatars = event.instructor_avatars ?? [];
  const visibleInstructors = instructorNames.slice(0, 2);
  const extraInstructors = Math.max(0, instructorNames.length - visibleInstructors.length);

  // Mirror mobile: 4 avatars in week view, 8 in day view.
  const maxStudents = view === 'day' ? 8 : 4;
  const { enrollments } = useClassEnrollments(event.event_id);
  const students = enrollments.filter((e) => e.registration_type !== 'waitlisted');
  const visibleStudents = students.slice(0, maxStudents);
  const extraStudents = Math.max(0, students.length - visibleStudents.length);

  const ringTone: 'card' | 'enrolled' = isEnrolled ? 'enrolled' : 'card';

  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-lg border p-2.5 transition-colors group ${
        isEnrolled
          ? 'bg-[#3B1A1F] border-[#9B1421]/60 hover:bg-[#4A1F25]'
          : 'bg-[#2A2626] border-[#504A4A] hover:bg-[#363232] hover:border-[#6A6464]'
      }`}
    >
      {/* Header: title + instructor avatar(s) + status */}
      <div className="flex items-start justify-between gap-1.5">
        <span className="text-[#EBFFEE] text-xs font-semibold leading-tight line-clamp-2 flex-1">
          {event.sport_name ?? 'Class'}
        </span>

        <div className="flex items-center gap-1 shrink-0">
          {visibleInstructors.length > 0 && (
            <div className="flex -space-x-1.5">
              {visibleInstructors.map((name, i) => {
                const coachId = event.instructor_ids?.[i];
                return (
                  <AvatarBubble
                    key={coachId ?? `coach-${event.event_id}-${i}`}
                    src={instructorAvatars[i]}
                    name={name}
                    size="md"
                    ring={ringTone}
                  />
                );
              })}
              {extraInstructors > 0 && (
                <div
                  className={`w-6 h-6 rounded-full ring-2 ${
                    ringTone === 'enrolled' ? 'ring-[#3B1A1F]' : 'ring-[#2A2626]'
                  } bg-[#504A4A] text-[#EBFFEE] text-[9px] font-semibold flex items-center justify-center`}
                  title={`+${extraInstructors} more`}
                >
                  +{extraInstructors}
                </div>
              )}
            </div>
          )}
          {isCheckedIn && <CheckCircle2 size={13} className="text-green-400" />}
        </div>
      </div>

      {/* Time */}
      <p className="text-[#A9A3A3] text-[11px] mt-0.5">
        {formatTime(event.start_time)} – {formatTime(event.end_time)}
      </p>

      {/* Tags row */}
      <div className="flex flex-wrap gap-1 mt-1.5">
        {event.skill_level_name && (
          <span className="text-[9px] bg-[#504A4A] text-[#EBFFEE] px-1.5 py-0.5 rounded-full">
            {event.skill_level_name}
          </span>
        )}
        {event.age_bracket_name && (
          <span className="text-[9px] bg-[#504A4A] text-[#EBFFEE] px-1.5 py-0.5 rounded-full">
            {event.age_bracket_name}
          </span>
        )}
        {isEnrolled && !isCheckedIn && (
          <span className="text-[9px] bg-[#9B1421] text-white px-1.5 py-0.5 rounded-full font-semibold">
            Enrolled
          </span>
        )}
      </div>

      {/* Location */}
      {event.location_name && (
        <span className="flex items-center gap-0.5 text-[#A9A3A3] text-[10px] truncate mt-1.5">
          <MapPin size={9} />
          {event.location_name}
        </span>
      )}

      {/* Footer: registered students banner + capacity */}
      <div className="flex items-center justify-between mt-1.5 gap-1.5">
        <div className="flex -space-x-1.5 min-h-[20px]">
          {visibleStudents.map((s, i) => (
            <AvatarBubble
              key={`student-${event.event_id}-${s.id}-${i}`}
              src={s.avatar_url}
              name={`${s.first_name ?? ''} ${s.last_name ?? ''}`.trim() || s.email || 'Student'}
              size="sm"
              ring={ringTone}
            />
          ))}
          {extraStudents > 0 && (
            <div
              className={`w-5 h-5 rounded-full ring-2 ${
                ringTone === 'enrolled' ? 'ring-[#3B1A1F]' : 'ring-[#2A2626]'
              } bg-[#504A4A] text-[#EBFFEE] text-[9px] font-semibold flex items-center justify-center`}
              title={`+${extraStudents} more`}
            >
              +{extraStudents}
            </div>
          )}
        </div>
        <span
          className={`text-[10px] shrink-0 font-medium ${capacityColor(
            event.current_registrations,
            event.max_capacity
          )}`}
        >
          {event.current_registrations ?? students.length}
          {event.max_capacity !== null ? `/${event.max_capacity}` : ''}
          {isFull && <span className="ml-0.5 font-semibold">Full</span>}
        </span>
      </div>
    </button>
  );
}
